/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { fetchUser, sendPresenceHeartbeat } from '../services/api';
import {
  clearStoredUser,
  getStoredUser,
  isAuthenticated,
  saveAuth,
  saveStoredUser,
} from '../services/auth';
import { RequestError } from '../services/httpClient';
import { UserInfo } from '../types';
import { reportClientError } from '../utils/error';

type AuthUserContextValue = {
  user: UserInfo | null;
  userLoading: boolean;
  setUser: (user: UserInfo | null) => void;
  refreshUser: () => Promise<void>;
};

const AuthUserContext = createContext<AuthUserContextValue | undefined>(undefined);
const PRESENCE_HEARTBEAT_INTERVAL_MS = 30_000;

export const AuthUserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUserState] = useState<UserInfo | null>(() => getStoredUser());
  const [userLoading, setUserLoading] = useState(true);

  const setUser = (nextUser: UserInfo | null) => {
    setUserState(nextUser);
    if (nextUser) {
      saveStoredUser(nextUser);
      return;
    }
    clearStoredUser();
  };

  const refreshUser = async () => {
    if (!isAuthenticated()) {
      try {
        const latestUser = await fetchUser({ suppressUnauthorizedRedirect: true });
        setUser(latestUser);
        saveAuth(latestUser.phone);
        return;
      } catch (error) {
        if (!(error instanceof RequestError && error.status === 401)) {
          reportClientError('auth.refresh_user', error);
        }
        setUser(null);
        return;
      }
    }

    try {
      const latestUser = await fetchUser();
      setUser(latestUser);
    } catch (error) {
      reportClientError('auth.refresh_user', error);
      setUser(null);
    }
  };

  useEffect(() => {
    let active = true;
    const bootstrap = async () => {
      if (!isAuthenticated()) {
        try {
          const latestUser = await fetchUser({ suppressUnauthorizedRedirect: true });
          if (active) {
            setUser(latestUser);
            saveAuth(latestUser.phone);
          }
        } catch (error) {
          if (!(error instanceof RequestError && error.status === 401)) {
            reportClientError('auth.bootstrap.restore', error);
          }
          if (active) {
            setUser(null);
          }
        } finally {
          if (active) {
            setUserLoading(false);
          }
        }
        return;
      }

      try {
        const latestUser = await fetchUser();
        if (active) {
          setUser(latestUser);
        }
      } catch (error) {
        reportClientError('auth.bootstrap', error);
        if (active) {
          setUser(null);
        }
      } finally {
        if (active) {
          setUserLoading(false);
        }
      }
    };
    bootstrap();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!user || !isAuthenticated()) {
      return;
    }

    let cancelled = false;
    const sendHeartbeat = async () => {
      try {
        await sendPresenceHeartbeat();
      } catch (error) {
        if (!cancelled) {
          reportClientError('presence.heartbeat', error);
        }
      }
    };

    void sendHeartbeat();

    const intervalId = window.setInterval(() => {
      void sendHeartbeat();
    }, PRESENCE_HEARTBEAT_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void sendHeartbeat();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  const value = useMemo<AuthUserContextValue>(
    () => ({
      user,
      userLoading,
      setUser,
      refreshUser,
    }),
    [user, userLoading]
  );

  return <AuthUserContext.Provider value={value}>{children}</AuthUserContext.Provider>;
};

export const useAuthUser = () => {
  const context = useContext(AuthUserContext);
  if (!context) {
    throw new Error('useAuthUser must be used within AuthUserProvider');
  }
  return context;
};
